import type { ReactNode } from 'react';

import type { FieldConfig } from '../config';
import type { SectionKey } from '../lease-details.types';

export const buildPath = (sectionKey: SectionKey, name: string, arrayIndex?: number): string =>
  arrayIndex == null ? name : `${sectionKey}.${arrayIndex}.${name}`;

// Resolves a label that may be a static string or an index-aware callback.
// Switch/Checkbox kinds use `ReactNode` labels and never pass functions; this
// only takes effect when a string-kind field carries a callback label.
export const resolveFieldLabel = (
  label: FieldConfig['label'],
  index: number | undefined,
): ReactNode => {
  if (typeof label === 'function') {
    return (label as (ctx: { index?: number }) => string)({ index });
  }
  return label;
};

export const resolveFieldHelperText = (
  helperText: FieldConfig['helperText'],
  index: number | undefined,
): ReactNode => {
  if (typeof helperText === 'function') {
    return (helperText as (ctx: { index?: number }) => ReactNode)({ index });
  }
  return helperText;
};

export const firstErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const node = error as { message?: unknown };
  // Skip empty-string messages and recurse — RHF can produce
  // `{ type: 'submit', message: '' }` for programmatic errors with no copy,
  // and bubbling that '' would shadow a real message from a sibling/child.
  if (typeof node.message === 'string' && node.message) return node.message;
  for (const key of Object.keys(error)) {
    const found = firstErrorMessage((error as Record<string, unknown>)[key]);
    if (found) return found;
  }
  return undefined;
};
